import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import studentPortalService from '../../services/studentPortalService';
import extraClassesService from '../../services/extraClassesService';
import { QuestionRenderer } from '../../components/assignments/QuestionRenderer';
import { CheckCircle, Clock, X, CaretLeft } from '@phosphor-icons/react';
import './StudentAssignmentSolver.css';

function StudentAssignmentSolver({ assignmentId, extraClassId, onClose, onSubmissionComplete }) {
  const [assignment, setAssignment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState('question');  // 'question' or 'review'
  const [autoGradeResult, setAutoGradeResult] = useState(null);

  const loadAssignment = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = extraClassId
        ? await extraClassesService.getAssignment(extraClassId, assignmentId)
        : await studentPortalService.getAssignment(assignmentId);
      setAssignment(data.assignment);
      setQuestions(data.assignment.questions || []);
      // Initialize answers - matching questions need objects, others need strings
      const initialAnswers = {};
      (data.assignment.questions || []).forEach(q => {
        initialAnswers[q.id] = q.type === 'matching' ? {} : '';
      });
      setAnswers(initialAnswers);
    } catch (error) {
      toast.error('Failed to load assignment');
    } finally {
      setLoading(false);
    }
  }, [assignmentId, extraClassId]);

  // Load assignment and questions
  useEffect(() => {
    loadAssignment();
  }, [assignmentId, extraClassId, loadAssignment]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSaveDraft = async () => {
    try {
      // Later: Implement save draft endpoint
      toast.success('Draft saved');
    } catch (error) {
      toast.error('Failed to save draft');
    }
  };

  const handleReviewAnswers = () => {
    // Check all required fields
    let allAnswered = true;
    questions.forEach(q => {
      if (!answers[q.id] || (typeof answers[q.id] === 'object' && Object.keys(answers[q.id]).length === 0)) {
        allAnswered = false;
      }
    });

    if (!allAnswered) {
      toast.error('Please answer all questions before reviewing');
      return;
    }

    setCurrentStep('review');
  };

  const handleSubmit = async () => {
    // Prevent submission if assignment is already graded
    if (assignment?.graded) {
      toast.error('This assignment has already been graded and cannot be submitted again');
      return;
    }

    setSubmitting(true);
    try {
      const response = extraClassId
        ? await extraClassesService.submitAssignment(extraClassId, assignmentId, { answers })
        : await studentPortalService.submitAssignment(assignmentId, answers);

      if (response.auto_graded && response.grading) {
        setAutoGradeResult(response.grading);
        toast.success('Assignment submitted and auto-graded!');
      } else {
        toast.success('Assignment submitted successfully');
      }

      // Callback to parent component
      if (onSubmissionComplete) {
        onSubmissionComplete(response);
      }

      // Close after 2 seconds if auto-graded
      if (response.auto_graded) {
        setTimeout(() => onClose?.(), 2000);
      } else {
        setTimeout(() => onClose?.(), 1500);
      }
    } catch (error) {
      toast.error('Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const getTimeRemaining = () => {
    if (!assignment || !assignment.due_date) return null;
    const dueDate = new Date(assignment.due_date);
    const now = new Date();
    const diffMs = dueDate - now;
    
    if (diffMs < 0) return 'OVERDUE';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const answeredCount = Object.values(answers).filter(a => a && (typeof a !== 'object' || Object.keys(a).length > 0)).length;
  const progressPercent = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

  if (loading) {
    return (
      <div style={styles.modalOverlay}>
        <div style={styles.modal}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p>Loading assignment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return null;
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <h1 style={styles.title}>{assignment.title}</h1>
            <p style={styles.subtitle}>
              {assignment.subject_name} | {questions.length} {questions.length === 1 ? 'question' : 'questions'} | {assignment.points_possible} points
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={24} weight="bold" />
          </button>
        </div>

        {/* Info Bar */}
        <div style={styles.infoBar}>
          <div style={styles.infoBitemLeft}>
            <Clock size={16} weight="bold" />
            <span><strong>Time remaining:</strong> {getTimeRemaining()}</span>
          </div>
          <div style={styles.infoBitemRight}>
            <span><strong>Progress:</strong> {answeredCount}/{questions.length} answered</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${progressPercent}%` }}></div>
          </div>
          <p style={styles.progressText}>{progressPercent}% Complete</p>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {currentStep === 'question' ? (
            // Questions Step
            <div>
              {questions.map((question, idx) => (
                <div key={question.id} style={styles.questionCard}>
                  <div style={styles.questionNumber}>
                    <span>{idx + 1}</span>
                    <span style={styles.questionCounter}>of {questions.length}</span>
                  </div>
                  
                  <QuestionRenderer
                    question={question}
                    answer={answers[question.id]}
                    onAnswerChange={handleAnswerChange}
                  />
                </div>
              ))}
            </div>
          ) : (
            // Review Step
            <div>
              <h2 style={styles.reviewTitle}>Review Your Answers</h2>
              
              {questions.map((question, idx) => {
                const answer = answers[question.id];
                const isAnswered = answer && (typeof answer !== 'object' || Object.keys(answer).length > 0);
                
                return (
                  <div key={question.id} style={styles.reviewItem}>
                    <div style={styles.reviewHeader}>
                      <div style={styles.reviewQuestion}>
                        <span style={styles.reviewQNumber}>Q{idx + 1}</span>
                        <span style={styles.reviewQText}>{question.question?.substring(0, 100)}</span>
                      </div>
                      {isAnswered ? (
                        <CheckCircle size={20} weight="fill" style={styles.reviewCheckmark} />
                      ) : (
                        <div style={styles.reviewUnanswered}>?</div>
                      )}
                    </div>
                    <div style={styles.reviewAnswer}>
                      {typeof answer === 'object' && answer !== null
                        ? Object.entries(answer)
                            .map(([left, right]) => `${left} → ${right}`)
                            .join(', ')
                        : String(answer).substring(0, 100)
                      }
                    </div>
                  </div>
                );
              })}

              {autoGradeResult && (
                <div style={styles.gradeResult}>
                  <h3 style={styles.gradeTitle}>Auto-graded Results</h3>
                  <div style={styles.gradeContent}>
                    <p><strong>Score:</strong> {autoGradeResult.score} / {autoGradeResult.max_score}</p>
                    <p><strong>Percentage:</strong> {autoGradeResult.percentage}%</p>
                    {autoGradeResult.feedback && (
                      <p style={styles.gradeFeedback}><strong>Feedback:</strong> {autoGradeResult.feedback}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div style={styles.footer}>
          {currentStep === 'question' ? (
            <>
              <button onClick={onClose} style={styles.btnSecondary}>
                Close
              </button>
              <button 
                onClick={handleReviewAnswers}
                style={styles.btnPrimary}
                disabled={answeredCount === 0}
              >
                Review Answers
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={() => setCurrentStep('question')}
                style={styles.btnSecondary}
              >
                <CaretLeft size={16} /> Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  ...styles.btnSuccess,
                  opacity: submitting ? 0.6 : 1,
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Assignment'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '24px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
  },
  headerContent: {
    flex: 1,
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '22px',
    fontWeight: '700',
    color: '#1f2937',
  },
  subtitle: {
    margin: 0,
    fontSize: '13px',
    color: '#6b7280',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s ease',
  },
  infoBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '13px',
    color: '#6b7280',
  },
  infoBitemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  infoBitemRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  progressContainer: {
    padding: '16px 24px',
    backgroundColor: '#fff',
  },
  progressBar: {
    width: '100%',
    height: '6px',
    backgroundColor: '#e5e7eb',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#3b82f6',
    transition: 'width 0.3s ease',
  },
  progressText: {
    margin: 0,
    fontSize: '12px',
    color: '#9ca3af',
    textAlign: 'right',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  questionCard: {
    marginBottom: '32px',
    paddingBottom: '32px',
    borderBottom: '1px solid #e5e7eb',
  },
  questionNumber: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: '500',
  },
  questionCounter: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  reviewTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937',
    margin: '0 0 20px 0',
  },
  reviewItem: {
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    marginBottom: '12px',
    border: '1px solid #e5e7eb',
  },
  reviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  reviewQuestion: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flex: 1,
  },
  reviewQNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    fontSize: '12px',
    fontWeight: '600',
    flexShrink: 0,
  },
  reviewQText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1f2937',
  },
  reviewCheckmark: {
    color: '#10b981',
    flexShrink: 0,
  },
  reviewUnanswered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    fontSize: '12px',
    fontWeight: '700',
    flexShrink: 0,
  },
  reviewAnswer: {
    fontSize: '13px',
    color: '#4b5563',
    backgroundColor: '#fff',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #e5e7eb',
  },
  gradeResult: {
    padding: '16px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    border: '1px solid #bbf7d0',
    marginTop: '20px',
  },
  gradeTitle: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#15803d',
  },
  gradeContent: {
    fontSize: '13px',
    color: '#166534',
  },
  gradeFeedback: {
    marginTop: '8px',
    fontSize: '13px',
    color: '#166534',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    justifyContent: 'flex-end',
  },
  btnPrimary: {
    padding: '10px 20px',
    borderRadius: '6px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.2s ease',
  },
  btnSuccess: {
    padding: '10px 20px',
    borderRadius: '6px',
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  btnSecondary: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
};

export default StudentAssignmentSolver;
